import React, { useContext, useEffect, useState } from 'react'
import { User } from '../models/User'
import useFetch from '../hooks/useFetch'
import { getAccessibleUsers, putGrantAdmin, putRevokeAdmin } from '../config/apiRoutes'
import SnackbarContext from '../contexts/SnackbarContext'
import { useTranslation } from 'react-i18next'
import UserContext from '../contexts/UserContext'
import PageContainer from '../containers/PageContainer'
import PageTitleCard from '../cards/PageTitleCard'
import PageTitleText from '../texts/PageTitleText'
import PrimaryButton from '../buttons/PrimaryButton'
import AddIcon from '@mui/icons-material/Add';
import ScrollableTable from '../table/ScrollableTable'
import TableHead from '../table/TableHead'
import TableHeadCell from '../table/TableHeadCell'
import UserRoleRow from '../user/UserRoleRow'
import ConfirmationDialog from '../modal/ConfirmationDialog'
import AddUserForm from '../user/AddUserForm'
import ResponsiveModal from '../modal/ResponsiveModal'

type Props = {}

const UsersPage = (props: Props) => {
    const { t } = useTranslation();
    const { selectedRestaurant } = useContext(UserContext);
    const [newUserOpen, setNewUserOpen] = useState(false);
    const [addAdminOpen, setAddAdminOpen] = useState(false);
    const [deleteUserOpen, setDeleteUserOpen] = useState(false);
    const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
    const loader = useFetch();
    const snackbar = useContext(SnackbarContext);
    const [users, setUsers] = useState<User[]>([]);

    useEffect(() => {
        document.title = `QuickPick - ${t("navigation.user_management")}`;
    }, [t("navigation.user_management")]);

    useEffect(() => {
        fetchRoles();
    }, [selectedRestaurant])

    async function fetchRoles() {
        if (selectedRestaurant == null) return null;
        const url = getAccessibleUsers.replace(":restaurantShortName", selectedRestaurant.restaurantShortName)
        const results = await loader.call<undefined, User[]>("GET", url);
        if (results.ok && results.data) {
            setUsers(results.data);
        }
        return null;
    }

    function handleAddAdminClick(user: User) {
        setSelectedUser(user);
        setAddAdminOpen(true);
    }

    function handleDeleteUserClick(user: User) {
        setSelectedUser(user);
        setDeleteUserOpen(true);
    }

    async function confirmDeleteUser() {
        if (selectedRestaurant == null || selectedUser == null) return;
        const url = putRevokeAdmin
            .replace(":restaurantShortName", selectedRestaurant.restaurantShortName)
            .replace(":userId", selectedUser.id.toString());
        const response = await loader.call("PUT", url, {});
        if (response.ok) {
            fetchRoles();
            setDeleteUserOpen(false);
            setSelectedUser(null);
            snackbar.showMessage(t("common.changes_saved"), "success");
        }
    }

    async function confirmAddAdmin() {
        if (selectedRestaurant == null || selectedUser == null) return;
        const url = putGrantAdmin
            .replace(":restaurantShortName", selectedRestaurant.restaurantShortName)
            .replace(":userId", selectedUser.id.toString());
        const response = await loader.call("PUT", url, {});
        if (response.ok) {
            fetchRoles();
            setAddAdminOpen(false);
            setSelectedUser(null);
            snackbar.showMessage(t("common.changes_saved"), "success");
        }
    }

    async function handleCreateUser() {
        if (selectedRestaurant == null) return;
        setNewUserOpen(false);
        snackbar.showMessage(t("common.changes_saved"), "success");
        fetchRoles();
    }

    return (
        <>
            <PageContainer>
                <PageTitleCard>
                    <div className="flex justify-between">
                        <div>
                            <PageTitleText>
                                {t("navigation.user_management")}
                            </PageTitleText>
                        </div>
                        <div>
                            <PrimaryButton
                                variant="strike"
                                startIcon={<AddIcon />}
                                onClick={() => setNewUserOpen(true)}
                            >
                                {t("common.add_user")}
                            </PrimaryButton>
                        </div>
                    </div>
                </PageTitleCard>
                <div className="mt-4">
                    <ScrollableTable>
                        <TableHead>
                            <TableHeadCell>
                                {t("auth.email")}
                            </TableHeadCell>
                            <TableHeadCell>
                                {t("auth.admin")}
                            </TableHeadCell>
                            <TableHeadCell>

                            </TableHeadCell>
                        </TableHead>
                        <tbody>
                            {users.map((user, i) => (
                                <UserRoleRow
                                    key={user.id}
                                    user={user}
                                    index={i}
                                    onDelete={() => handleDeleteUserClick(user)}
                                    onGrantAdmin={() => handleAddAdminClick(user)}
                                />
                            ))}
                        </tbody>
                    </ScrollableTable>
                </div>
            </PageContainer>
            <ConfirmationDialog
                open={addAdminOpen}
                onClose={() => setAddAdminOpen(false)}
                texts={{
                    title: t("auth.grant_admin_title"),
                    confirm: t("common.grant"),
                    cancel: t("common.cancel")
                }}
                onConfirm={confirmAddAdmin}
                onCancel={() => setAddAdminOpen(false)}
            >
                <>
                    <div>
                        {t("auth.grant_admin_confirmation")}
                    </div>
                    <div>
                        {t("auth.selected_user")}: <strong className="text-primary-dark">{selectedUser?.email}</strong>
                    </div>
                </>
            </ConfirmationDialog >
            <ConfirmationDialog
                open={deleteUserOpen}
                onClose={() => setDeleteUserOpen(false)}
                texts={{
                    title: t("auth.delete_user_title"),
                    confirm: t("common.delete"),
                    cancel: t("common.cancel")
                }}
                onConfirm={confirmDeleteUser}
                onCancel={() => setDeleteUserOpen(false)}            >
                <div>
                    <div>
                        {t("auth.delete_user_confirmation")}
                    </div>
                    <div>
                        {t("auth.selected_user")}: <strong className="text-primary-dark">{selectedUser?.email}</strong>
                    </div>
                </div>
            </ConfirmationDialog>
            <ResponsiveModal
                open={newUserOpen}
                handleClose={() => setNewUserOpen(false)}
            >
                <>
                    <AddUserForm
                        onUserAdded={handleCreateUser}
                        onCancel={() => setNewUserOpen(false)}
                    />
                </>
            </ResponsiveModal>
        </>
    )
}

export default UsersPage